import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

function isRequestAbort(error: unknown, request: Request): boolean {
  if (request.signal.aborted) return true;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return error instanceof Error && error.name === "AbortError";
}

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    // Closing a tab, navigating during SSR, or an HMR refresh aborts the
    // in-flight request. That is expected control flow, not an application
    // failure, so let TanStack stop rendering instead of manufacturing a 500.
    if (isRequestAbort(error, request)) {
      throw error;
    }
    // Auth and redirects intentionally throw Web Responses. Preserve them so
    // TanStack can serialize the real status instead of turning a 401 into a
    // branded 500 page (which surfaces in the browser as "[object Response]").
    if (
      error instanceof Response ||
      (error != null && typeof error === "object" && "statusCode" in error)
    ) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));
